/**
 * HTTP layer for Foundation API calls.
 * Handles request construction, response unwrapping, and error extraction.
 */

/**
 * Unwrap backend response envelope.
 * The API wraps responses in { response: ... } or { data: ... }.
 */
export function unwrapResponse(json: Record<string, unknown>): unknown {
  if (json?.response !== undefined) return json.response
  if (json?.data !== undefined) return json.data
  return json
}

/**
 * Extract a meaningful error from a failed API response.
 */
export function extractError(status: number, body: Record<string, unknown>): Error {
  const errorObj = body?.error as Record<string, unknown> | undefined

  if (errorObj) {
    let message = errorObj.message as string || 'Unknown error'
    const details = errorObj.details as Array<{ field?: string; message?: string }> | undefined
    if (details?.length) {
      const detailMessages = details
        .map(d => d.field ? `${d.field}: ${d.message}` : d.message)
        .join(', ')
      message = message ? `${message} ${detailMessages}` : detailMessages
    }
    return Object.assign(new Error(message), {
      code: errorObj.code,
      type: errorObj.code,
      details,
      status
    })
  }

  const customMessage = (body?.data as Record<string, unknown>)?.message || body?.message
  if (customMessage) {
    return Object.assign(new Error(customMessage as string), { status })
  }

  return Object.assign(new Error(`HTTP ${status}`), { status })
}

export interface HttpClientConfig {
  appId: string
  tenantId: string
  version: string
  getToken: () => Promise<string>
}

export function createHttpClient(config: HttpClientConfig) {
  function headers(token: string): Record<string, string> {
    return {
      'X-Foundation-Mvp-Application-Id': config.appId,
      'X-Foundation-Mvp-Tenant-Id': config.tenantId,
      'X-Foundation-Mvp-Application-Version': config.version,
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  }

  async function request(
    baseUrl: string,
    path: string,
    method: string,
    options: { params?: Record<string, unknown>; body?: unknown } = {}
  ): Promise<unknown> {
    const token = await config.getToken()
    const hdrs = headers(token)

    let url = `${baseUrl}${path}`
    if (options.params) {
      const searchParams = new URLSearchParams()
      for (const [key, value] of Object.entries(options.params)) {
        if (value !== undefined && value !== null) {
          searchParams.set(key, String(value))
        }
      }
      const qs = searchParams.toString()
      if (qs) url += `?${qs}`
    }

    const fetchOptions: RequestInit = { method, headers: hdrs }
    if (options.body !== undefined) {
      fetchOptions.body = JSON.stringify(options.body)
    }

    const response = await fetch(url, fetchOptions)

    if (!response.ok) {
      let body: Record<string, unknown> = {}
      try { body = await response.json() } catch { /* no json body */ }
      throw extractError(response.status, body)
    }

    if (response.status === 204) return null

    const json = await response.json()
    return unwrapResponse(json as Record<string, unknown>)
  }

  /** Raw fetch with auth headers but no unwrapping (for S3 uploads etc.) */
  async function rawFetch(url: string, options: RequestInit = {}): Promise<Response> {
    return fetch(url, options)
  }

  return { request, rawFetch, headers }
}

export type HttpClient = ReturnType<typeof createHttpClient>
